/* assets/backorder-refresh.js
   FoxTheme-safe backorder notice refresher:
   - No MutationObserver (prevents UI thrash / spinner weirdness)
   - Listens to FoxTheme pubsub cartUpdate event (the theme’s native signal)
   - Updates warnings live while user clicks +/- without triggering cart refresh loops
*/
(() => {
  const CART_SELECTOR = ".cart";
  const ITEM_SELECTOR = ".cart-item";
  const NOTICE_SELECTOR = "[data-backorder-notice]";
  const MESSAGE_SELECTOR = "[data-backorder-message]";
  const ALERT_SELECTOR = ".alert--warning";

  const toInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const setHidden = (el, hidden) => {
    if (!el) return;
    if (hidden) el.setAttribute("hidden", "");
    else el.removeAttribute("hidden");
  };

  const getQty = (cartItem) => {
    const input = cartItem.querySelector('input[name="updates[]"]');
    const v = input ? toInt(input.value) : null;
    return v ?? 0;
  };

  const updateItem = (cartItem) => {
    const notice = cartItem.querySelector(NOTICE_SELECTOR);
    if (!notice) return;

    const alertEl = notice.querySelector(ALERT_SELECTOR);
    const msgEl = notice.querySelector(MESSAGE_SELECTOR);
    if (!alertEl || !msgEl) return;

    const available = toInt(notice.getAttribute("data-available"));
    const policy = (notice.getAttribute("data-policy") || "continue").toLowerCase();
    const isSpecialOrder = notice.getAttribute("data-special-order") === "true";

    if (isSpecialOrder) {
      msgEl.textContent = "Special order item. Order now and we\u2019ll confirm timing.";
      setHidden(alertEl, false);
      setHidden(notice, false);
      return;
    }

    if (available === null) {
      msgEl.textContent = "";
      setHidden(alertEl, true);
      setHidden(notice, true);
      return;
    }

    const requested = getQty(cartItem);
    const shouldShow = requested > available;

    if (!shouldShow) {
      msgEl.textContent = "";
      setHidden(alertEl, true);
      setHidden(notice, true);
      return;
    }

    if (policy === "continue" && available <= 0) {
      msgEl.textContent = "This item is sold out and orders will be placed as a backorder.";
    } else if (policy === "continue") {
      const backorderQty = requested - available;
      msgEl.textContent = `Only ${available} available now. ${backorderQty} will go to backorder.`;
    } else if (available <= 0) {
      msgEl.textContent = "Currently unavailable. Please contact us for further info.";
    } else {
      msgEl.textContent = `Only ${available} available. Please reduce quantity to continue.`;
    }

    setHidden(alertEl, false);
    setHidden(notice, false);
  };

  const updateAll = (root) => {
    root.querySelectorAll(ITEM_SELECTOR).forEach(updateItem);
  };

  // Small rAF debounce so rapid +/- clicks don't cause layout thrash
  const rafDebounce = (() => {
    let raf = null;
    return (fn) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        fn();
      });
    };
  })();

  const install = () => {
    const cartRoot = document.querySelector(CART_SELECTOR);
    if (!cartRoot) return;

    // Initial pass
    updateAll(cartRoot);

    // Live updates while user changes qty (no network calls)
    cartRoot.addEventListener("input", (e) => {
      const t = e.target;
      if (!t?.matches?.('input[name="updates[]"]')) return;
      const item = t.closest(ITEM_SELECTOR);
      if (!item) return;
      rafDebounce(() => updateItem(item));
    });

    cartRoot.addEventListener("click", (e) => {
      const btn = e.target?.closest?.(".quantity__button");
      if (!btn) return;
      const item = btn.closest(ITEM_SELECTOR);
      if (!item) return;

      // cart.js updates the input value asynchronously, so tick once after it runs
      setTimeout(() => updateItem(item), 0);
      setTimeout(() => updateItem(item), 80);
    });

    // Hook into FoxTheme cart update pubsub (this is the KEY part)
    const trySubscribe = () => {
      const pubsub = window.FoxTheme?.pubsub;
      const events = pubsub?.PUB_SUB_EVENTS;
      const subscribe = pubsub?.subscribe;

      if (!pubsub || !events || typeof subscribe !== "function") return false;

      // When the theme finishes refreshing cart HTML, update all notices
      subscribe(events.cartUpdate, () => {
        // cart DOM has been swapped; re-grab root and update
        const freshRoot = document.querySelector(CART_SELECTOR);
        if (freshRoot) updateAll(freshRoot);
      });

      return true;
    };

    // Try immediately, and retry a few times in case FoxTheme loads late
    if (!trySubscribe()) {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (trySubscribe() || attempts >= 20) clearInterval(timer);
      }, 150);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", install);
  } else {
    install();
  }
})();
